import React, { useState, useEffect } from 'react';
import { 
  UserPlus,
  Search,
  Calendar,
  Edit,
  Trash2,
  ChevronDown,
  CheckCircle,
  Mail,
  Check
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { PageLayout } from '../components/layout';
import { UpgradeModal } from '../components/modals';
import { MemberAvatar } from '../components/teams/MemberAvatar';
import { BrandAccessBadge } from '../components/teams/BrandAccessBadge';
import { BrandAccessManager } from '../components/teams/BrandAccessManager';
import { RoleSelect } from '../components/common/RoleSelect';
import { BrandSelector } from '../components/common/BrandSelector';
import { MemberModal } from '../components/teams/MemberModal';
import { DeleteConfirmationModal } from '../components/common/DeleteConfirmationModal';
import { RemoveMemberModal } from '../components/teams/RemoveMemberModal';
import { useTeamMembers } from '../hooks/useTeamMembers';
import { useQueryClient } from '@tanstack/react-query';
import { auth } from '../config/firebase';
import { useInviteTeamMembers } from '../hooks/useInviteTeamMembers';
import { StatusTag } from '../components/common/StatusTag';
import { RoleTag } from '../components/common/RoleTag';
import { useBrands } from '../hooks/useBrands';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { useUrlStringState } from '../hooks/useUrlState';

// Define interfaces based on API response
interface Brand {
  id: string;
  name: string;
  role: 'reviewer' | 'writer' | 'admin' | 'manager'; // Assuming these roles, adjust if needed
  status: 'active' | 'pending';
  logo?: string; // Add logo property for BrandAccessManager/BrandSelector
  logoUrl?: string; // Add logoUrl property to match API and BrandAccessBadge
}

interface Member {
  id: string;
  email: string;
  name: string;
  avatarUrl: string;
  addedAt: string;
  brands: Brand[];
  status: 'active' | 'pending';
  role: 'reviewer' | 'writer' | 'admin' | 'manager';
  invitationId?: string;
  updatedAt: string;
}

// Mock data that would typically come from an API - REMOVE THIS
/*
const mockMembers = [
  // ... removed mock data ...
];
*/

const TeamMembers = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const inviteMutation = useInviteTeamMembers();
  const [activeTab, setActiveTab] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [filterRole, setFilterRole] = useState('all');
  const [expandedMembers, setExpandedMembers] = useState<Record<string, boolean>>({});
  
  // Use URL state to manage modal visibility
  const [modalParam, setModalParam] = useUrlStringState('modal', '');
  
  // Derive modal state from URL parameter
  const isAddModalOpen = modalParam === 'invite';
  const isUpgradeModalOpen = modalParam === 'upgrade';
  
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isRemoveModalOpen, setIsRemoveModalOpen] = useState(false);
  const [selectedMember, setSelectedMember] = useState<Member | null>(null);
  const [isModifyModalOpen, setIsModifyModalOpen] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);
  const [showSuccessAnimation, setShowSuccessAnimation] = useState(false);
  const [successInviteDetails, setSuccessInviteDetails] = useState<{emails: string[], brandName: string, action?: 'invite' | 'delete'} | null>(null);
  const [resendingInvitations, setResendingInvitations] = useState<Record<string, boolean>>({});
  const [acceptingInvitations, setAcceptingInvitations] = useState<Record<string, boolean>>({});

  useDocumentTitle(t('teamMembers.title', 'Team Members'));



  const { data: teamData, isLoading, error: fetchError } = useTeamMembers();
  const { data: brandsData, isLoading: brandsLoading, error: brandsFetchError } = useBrands();

  const members = teamData?.members || [];
  const totalUsers = teamData?.pagination?.total ?? members.length;
  const userLimits = (teamData as any)?.userLimits ?? (teamData as any)?.usersLimit ?? undefined;

  // Get all brands from the brands API for inviting new members
  const allAvailableBrands = React.useMemo(() => {
    if (!brandsData?.brands) return [];
    return brandsData.brands.map(brand => ({
      id: brand.id,
      name: brand.name,
      role: 'writer' as const, // Default role for new invitations
      status: 'active' as const, // Default status
      logo: brand.logo,
      logoUrl: brand.logo
    }));
  }, [brandsData]);

  // Get brands that are currently used by team members (for display purposes)
  const brandsUsedByMembers = React.useMemo(() => {
    const brandsMap = new Map<string, Brand>();
    members.forEach(member => {
      member.brands.forEach(brand => {
        if (!brandsMap.has(brand.id)) {
          brandsMap.set(brand.id, {
            ...brand, // Spread existing brand data (this should include logoUrl if present)
          });
        }
      });
    });
    return Array.from(brandsMap.values());
  }, [members]);

  const getDisplayName = (member: Member) => {
    if (member.name && member.name.trim() !== '') {
      return member.name;
    }
    const emailName = member.email.split('@')[0];
    return emailName
      .split('.')
      .map((part: string) => part.charAt(0).toUpperCase() + part.slice(1))
      .join(' ');
  };

  const filteredMembers = React.useMemo(() => {
    return members.filter(member => {
      const lowerSearchQuery = searchQuery.toLowerCase();
      const matchesSearch = !searchQuery || 
                            member.email.toLowerCase().includes(lowerSearchQuery) ||
                            getDisplayName(member).toLowerCase().includes(lowerSearchQuery);
                            
      if (!matchesSearch) return false;

      const matchesRole = filterRole === 'all' || member.brands.some(brand => brand.role === filterRole);
      if (!matchesRole) return false;
      
      // Filter by activeTab status - check member-level status first, then brand-level status as fallback
      if (activeTab === 'all') {
        return true;
      }
      
      // Check member-level status first (from API response)
      if (member.status && member.status === activeTab) {
        return true;
      }
      
      // Fallback to brand-level status
      return member.brands.some(brand => brand.status === activeTab);
    });
  }, [members, searchQuery, activeTab, filterRole]);

  const handleDelete = (member: Member) => {
    // Check if member is an admin - prevent deletion
    const isAdmin = member.brands.some(brand => brand.role === 'admin');
    if (isAdmin) {
      setLocalError(t('teamMembers.errors.cannotDeleteAdmin', 'Cannot delete an admin member. Please change their role first.'));
      return;
    }
    
    setSelectedMember(member);
    setIsRemoveModalOpen(true);
    setLocalError(null);
  };

  const handleConfirmDelete = async () => {
    if (!selectedMember) return;
    setLocalError(null);

    let token: string | null | undefined = null;
    try {
      token = await auth.currentUser?.getIdToken();
      if (!token) {
        throw new Error("Authentication token is missing.");
      }
    } catch (tokenError) {
      console.error("Error getting auth token:", tokenError);
      setLocalError(tokenError instanceof Error ? tokenError.message : "Failed to get auth token");
      return;
    }

    try {
      let deleteUrl = '';
      
      // If member has an invitationId (pending), delete the invitation
      if (selectedMember.invitationId) {
        deleteUrl = `https://api.socialness.ai/management/teams/invitations/${selectedMember.invitationId}`;
        console.log('Attempting to delete invitation:', selectedMember.invitationId);
      } else {
        // For active members, use the members endpoint with brandId
        const brandId = selectedMember.brands.length > 0 ? selectedMember.brands[0].id : '';
        deleteUrl = `https://api.socialness.ai/management/teams/members/${selectedMember.id}?brandId=${brandId}`;
        console.log('Attempting to delete member:', selectedMember.id, 'from brand:', brandId);
      }

      const response = await fetch(deleteUrl, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        let errorBody = `Failed to delete ${selectedMember.invitationId ? 'invitation' : 'member'} (status: ${response.status})`;
         try {
          const errorData = await response.json();
          errorBody = errorData.message || JSON.stringify(errorData) || errorBody;
        } catch (e) { /* Ignore */ }
        throw new Error(errorBody);
      }
      
      console.log(`${selectedMember.invitationId ? 'Invitation' : 'Member'} deleted successfully:`, selectedMember.invitationId || selectedMember.id);
      
      // Show success animation
      const brandName = selectedMember.brands.length > 0 ? selectedMember.brands[0].name : 'Team';
      setSuccessInviteDetails({
        emails: [selectedMember.email],
        brandName: brandName,
        action: 'delete'
      });
      setShowSuccessAnimation(true);
      
      setIsDeleteModalOpen(false); 
      setSelectedMember(null); 
      
      // Auto hide animation after 4 seconds
      setTimeout(() => {
        setShowSuccessAnimation(false);
      }, 4000);
      
      await queryClient.invalidateQueries({ queryKey: ['teamMembers'] });

    } catch (deleteError) {
      console.error('Error deleting:', deleteError);
      setLocalError(deleteError instanceof Error ? deleteError.message : "An unknown error occurred during deletion.");
    }
  };

  const handleModify = (member: Member) => {
    setSelectedMember(member);
    setIsModifyModalOpen(true);
    setLocalError(null);
  };

  const handleResendInvitation = async (member: Member) => {
    if (!member.invitationId) return;
    
    setLocalError(null);
    setResendingInvitations(prev => ({ ...prev, [member.id]: true }));

    try {
      const token = await auth.currentUser?.getIdToken();
      if (!token) {
        throw new Error("Authentication token is missing.");
      }

      console.log('Resending invitation for:', member.email, 'invitationId:', member.invitationId);
      
      const response = await fetch('https://api.socialness.ai/management/teams/invitations/resend', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          invitationId: member.invitationId
        })
      });

      if (!response.ok) {
        let errorBody = `Failed to resend invitation (status: ${response.status})`;
        try {
          const errorData = await response.json();
          errorBody = errorData.message || JSON.stringify(errorData) || errorBody;
        } catch (e) { /* Ignore */ }
        throw new Error(errorBody);
      }
      
      const result = await response.json();
      console.log('Invitation resent successfully:', result);
      
      // Show success message - use the first brand name if available
      const brandName = member.brands.length > 0 ? member.brands[0].name : 'Team';
      setSuccessInviteDetails({
        emails: [member.email],
        brandName: brandName,
        action: 'invite'
      });
      setShowSuccessAnimation(true);
      
      // Auto hide animation after 4 seconds
      setTimeout(() => {
        setShowSuccessAnimation(false);
      }, 4000);

    } catch (error) {
      console.error('Error resending invitation:', error);
      setLocalError(error instanceof Error ? error.message : "Failed to resend invitation.");
    } finally {
      setResendingInvitations(prev => ({ ...prev, [member.id]: false }));
    }
  };

  const handleAcceptInvitation = async (member: Member) => {
    if (!member.invitationId) return;
    
    setLocalError(null);
    setAcceptingInvitations(prev => ({ ...prev, [member.id]: true }));

    try {
      const token = await auth.currentUser?.getIdToken();
      if (!token) {
        throw new Error("Authentication token is missing.");
      }

      console.log('Accepting invitation for:', member.email, 'invitationId:', member.invitationId);
      
      const response = await fetch('https://api.socialness.ai/management/teams/invitations/accept', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          invitationId: member.invitationId
        })
      });

      if (!response.ok) {
        let errorBody = `Failed to accept invitation (status: ${response.status})`;
        try {
          const errorData = await response.json();
          errorBody = errorData.message || JSON.stringify(errorData) || errorBody;
        } catch (e) { /* Ignore */ }
        throw new Error(errorBody);
      }
      
      const result = await response.json();
      console.log('Invitation accepted successfully:', result);
      
      // Show success message - use the first brand name if available
      const brandName = member.brands.length > 0 ? member.brands[0].name : 'Team';
      setSuccessInviteDetails({
        emails: [member.email],
        brandName: brandName,
        action: 'invite'
      });
      setShowSuccessAnimation(true);
      
      // Auto hide animation after 4 seconds
      setTimeout(() => {
        setShowSuccessAnimation(false);
      }, 4000);

      // Refresh the team members list
      await queryClient.invalidateQueries({ queryKey: ['teamMembers'] });

    } catch (error) {
      console.error('Error accepting invitation:', error);
      setLocalError(error instanceof Error ? error.message : "Failed to accept invitation.");
    } finally {
      setAcceptingInvitations(prev => ({ ...prev, [member.id]: false }));
    }
  };

  const handleOpenInviteModal = () => {
    setModalParam('invite');
    setLocalError(null); // Clear any previous errors when opening modal
  };

  const handleCloseInviteModal = () => {
    setModalParam('');
    if (!inviteMutation.isPending) {
      inviteMutation.reset();
    }
  };

  if (isLoading || brandsLoading) {
    // Skeleton Loader for Team Members Table
    return (
      <div className="min-h-screen bg-white lg:ml-16 pt-4 lg:pt-0">
        <div className="w-[95%] mx-auto py-8">
          {/* Header Skeleton */}
          <div className="mb-8">
            <div className="h-8 bg-gray-200 rounded w-1/3 mb-2 animate-pulse"></div>
            <div className="h-4 bg-gray-200 rounded w-3/4 animate-pulse"></div>
          </div>
          {/* Filters Skeleton */}
          <div className="mb-6 flex flex-col md:flex-row gap-4 animate-pulse">
            <div className="h-10 bg-gray-200 rounded-lg md:flex-1"></div>
            <div className="h-10 bg-gray-200 rounded-lg w-full md:w-64"></div>
          </div>
          {/* Tabs Skeleton */}
          <div className="mb-6 h-12 bg-gray-200 rounded animate-pulse"></div>
          {/* Table Skeleton */}
          <div className="bg-white shadow-lg border border-gray-200 rounded-xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    {/* Header Cells */}
                    {[1, 2, 3, 4].map(i => (
                      <th key={i} scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        <div className="h-4 bg-gray-200 rounded w-1/2 animate-pulse"></div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {/* Table Row Skeletons */}
                  {[1, 2, 3, 4, 5].map((i) => (
                    <tr key={i} className="animate-pulse">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="h-10 w-10 bg-gray-200 rounded-full mr-4"></div>
                          <div>
                            <div className="h-4 bg-gray-200 rounded w-24 mb-1"></div>
                            <div className="h-3 bg-gray-200 rounded w-32"></div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex flex-wrap gap-2">
                          <div className="h-6 w-28 bg-gray-200 rounded-full"></div>
                          <div className="h-6 w-24 bg-gray-200 rounded-full"></div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap hidden sm:table-cell">
                        <div className="h-4 bg-gray-200 rounded w-20"></div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        <div className="flex justify-end gap-2">
                          <div className="h-5 w-5 bg-gray-200 rounded"></div>
                          <div className="h-5 w-5 bg-gray-200 rounded"></div>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (fetchError || brandsFetchError) {
    const error = fetchError || brandsFetchError;
    return (
      <div className="min-h-screen bg-white lg:ml-16 pt-4 lg:pt-0 flex flex-col items-center justify-center text-center px-4">
        <p className="text-red-600 font-semibold">Error loading data:</p>
        <p className="text-red-500 mt-2">{error?.message}</p>
        <button 
            onClick={() => window.location.reload()}
            className="mt-4 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
          >
            Try Again
          </button>
      </div>
    );
  }

  return (
    <>
      <PageLayout
      title={t('teamMembers.title')}
      subtitle={t('teamMembers.subtitle')}
      actions={
        <div className="flex flex-col items-end gap-2">
          {localError && !isAddModalOpen && (
            <div className="text-red-600 text-sm bg-red-50 px-3 py-2 rounded-lg">
              {localError}
            </div>
          )}
          <button 
            onClick={() => {
              const atLimit = typeof userLimits === 'number' && totalUsers >= userLimits;
              if (atLimit) {
                setModalParam('upgrade');
              } else {
                handleOpenInviteModal();
              }
            }}
            className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50"
            disabled={inviteMutation.isPending}
          >
            <UserPlus className="w-5 h-5 mr-2" />
            {inviteMutation.isPending ? t('common.loading', 'Loading...') : t('teamMembers.addMember')}
          </button>
        </div>
      }
    >

          <div className="mb-6">
            <div className="flex items-center gap-2">
              <div className="relative flex-1">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Search className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg shadow-sm placeholder-gray-400 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                  placeholder={t('teamMembers.searchPlaceholder')}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
              <div className="relative flex-shrink-0 w-44 md:w-64">
                <div className="absolute -top-5 right-0 text-black text-xs md:text-sm font-semibold leading-none whitespace-nowrap">
                  {typeof userLimits === 'number'
                    ? `${totalUsers}/${userLimits} ${t('billing.features.users_plural', 'team members')}`
                    : `${totalUsers} ${t('billing.features.users_plural', 'team members')}`}
                </div>
                <RoleSelect
                  value={filterRole}
                  onChange={setFilterRole}
                  isFilter={true}
                  className="w-full"
                />
              </div>
            </div>
          </div>

          <div className="mb-6 overflow-x-auto">
            <nav className="flex space-x-3 sm:space-x-4 md:space-x-6 border-b border-gray-200" aria-label="Tabs">
              {['all', 'active', 'pending'].map((tab) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`${
                    activeTab === tab
                      ? 'border-indigo-500 text-indigo-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  } whitespace-nowrap py-2 sm:py-3 md:py-4 px-1 border-b-2 font-medium text-xs md:text-sm flex items-center gap-1 sm:gap-2`}
                >
                  {tab === 'active' && (
                    <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 rounded-full bg-green-500" />
                  )}
                  {tab === 'pending' && (
                    <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 rounded-full bg-orange-500" />
                  )}
                  {t(`teamMembers.tabs.${tab}`)}
                </button>
              ))}
            </nav>
          </div>

          <div className="space-y-4 pb-8">
            <div className="bg-white shadow-lg border border-black rounded-xl overflow-hidden">
              <style>{`
                @media (max-width: 639px) {
                  .team-table th:nth-child(1), .team-table td:nth-child(1) { width: 50% !important; }
                  .team-table th:nth-child(2), .team-table td:nth-child(2) { width: 35% !important; }
                  /* Actions column is the 5th cell when hidden columns exist on mobile */
                  .team-table th:nth-child(5), .team-table td:nth-child(5) { width: 15% !important; }
                }
                @media (min-width: 640px) {
                  /* Desktop/tablet widths now that Org Role column is present */
                  .team-table th:nth-child(1), .team-table td:nth-child(1) { width: 35% !important; }
                  .team-table th:nth-child(2), .team-table td:nth-child(2) { width: 30% !important; }
                  .team-table th:nth-child(3), .team-table td:nth-child(3) { width: 15% !important; }
                  .team-table th:nth-child(4), .team-table td:nth-child(4) { width: 10% !important; }
                  .team-table th:nth-child(5), .team-table td:nth-child(5) { width: 10% !important; }
                }
              `}</style>
              <div className="overflow-x-auto">
                <table className="w-full divide-y divide-gray-200 team-table" style={{ tableLayout: 'fixed' }}>
                  <thead className="bg-gray-50">
                    <tr>
                      <th scope="col" className="px-2 sm:px-3 md:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t('teamMembers.table.member')}
                      </th>
                      <th scope="col" className="px-2 sm:px-3 md:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t('teamMembers.table.brands')}
                      </th>
                      <th scope="col" className="px-2 sm:px-3 md:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden sm:table-cell">
                        {t('teamMembers.table.orgRole', 'Org. Role')}
                      </th>
                      <th scope="col" className="px-2 sm:px-3 md:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden sm:table-cell">
                        {t('teamMembers.table.joined')}
                      </th>
                      <th scope="col" className="relative px-2 sm:px-3 md:px-6 py-2 sm:py-3">
                        <span className="sr-only">{t('teamMembers.table.actions')}</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredMembers.length === 0 ? (
                      <tr>
                        <td colSpan={4} className="px-6 py-8 text-center text-gray-500">
                          {searchQuery || filterRole !== 'all' || activeTab !== 'all' 
                            ? t('teamMembers.noResults') 
                            : t('teamMembers.noMembers')}
                        </td>
                      </tr>
                    ) : (
                      filteredMembers.map((member) => (
                        <React.Fragment key={member.id}>
                          <tr className={`hover:bg-gray-50 ${expandedMembers[member.id] ? 'bg-gray-50' : ''}`}>
                            <td className="px-2 sm:px-3 md:px-6 py-2 sm:py-3 md:py-4">
                              <div className="flex items-center">
                                <button 
                                  onClick={() => setExpandedMembers(prev => ({
                                    ...prev,
                                    [member.id]: !prev[member.id]
                                  }))}
                                  className="mr-1 text-gray-400 hover:text-gray-600 p-1 flex-shrink-0"
                                  aria-label={expandedMembers[member.id] ? "Collapse details" : "Expand details"}
                                >
                                  <ChevronDown 
                                    size={12} 
                                    className={`transform transition-transform ${expandedMembers[member.id] ? 'rotate-180' : ''}`} 
                                  />
                                </button>
                                <div className="flex-shrink-0 h-7 w-7 sm:h-8 sm:w-8 md:h-10 md:w-10 mr-3">
                                  <MemberAvatar
                                    name={member.name}
                                    email={member.email}
                                    avatarUrl={member.avatarUrl}
                                  />
                                </div>
                                <div className="min-w-0 flex-1">
                                  <div className="text-xs md:text-sm font-medium text-gray-900 truncate">{getDisplayName(member)}</div>
                                  <div className="text-xs md:text-sm text-gray-500 truncate">{member.email}</div>
                                </div>
                              </div>
                            </td>
                            <td className="px-2 sm:px-3 md:px-6 py-2 sm:py-3 md:py-4">
                              <div className="flex flex-wrap gap-1">
                                {member.brands.map((brand, index) => (
                                  <BrandAccessBadge
                                    key={index}
                                    brand={brand}
                                    role={brand.role}
                                    status={brand.status}
                                  />
                                ))}
                              </div>
                            </td>
                            <td className="px-2 sm:px-3 md:px-6 py-2 sm:py-3 md:py-4 whitespace-nowrap text-xs md:text-sm text-gray-700 hidden sm:table-cell">
                              {member.orgRole 
                                ? member.orgRole
                                    .replace('_', ' ')
                                    .replace(/^./, (s) => s.toUpperCase())
                                : '-'}
                            </td>
                            <td className="px-2 sm:px-3 md:px-6 py-2 sm:py-3 md:py-4 whitespace-nowrap text-xs md:text-sm text-gray-500 hidden sm:table-cell">
                              <div className="flex items-center">
                                <Calendar className="flex-shrink-0 mr-1 sm:mr-1.5 h-3 w-3 sm:h-4 sm:w-4 text-gray-400" />
                                {member.addedAt ? new Date(member.addedAt).toLocaleDateString() : 'N/A'}
                              </div>
                            </td>
                            <td className="px-2 sm:px-3 md:px-6 py-2 sm:py-3 md:py-4 whitespace-nowrap text-right text-xs md:text-sm font-medium">
                              <div className="flex items-center justify-end gap-1">
                                {member.status === 'pending' && member.invitationId && (
                                  <button 
                                    onClick={() => handleResendInvitation(member)}
                                    disabled={resendingInvitations[member.id]}
                                    className="p-1.5 sm:p-2 rounded-lg hover:bg-blue-100 transition-colors group relative disabled:opacity-50 disabled:cursor-not-allowed"
                                    aria-label={t('teamMembers.actions.resendInvitationAriaLabel', { email: member.email })}
                                  >
                                    <Mail className={`w-3.5 h-3.5 sm:w-4 sm:h-4 text-blue-600 group-hover:text-blue-700 ${resendingInvitations[member.id] ? 'animate-pulse' : ''}`} />
                                    {/* Custom tooltip */}
                                    <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-50 whitespace-nowrap">
                                      {t('teamMembers.actions.resendInvitation', 'Resend invitation')}
                                      <div className="absolute top-full left-1/2 -translate-x-1/2 w-0 h-0 border-l-2 border-r-2 border-t-2 border-l-transparent border-r-transparent border-t-blue-100"></div>
                                    </div>
                                  </button>
                                )}
                                <button 
                                  onClick={() => handleModify(member)}
                                  className="p-1.5 sm:p-2 rounded-lg hover:bg-purple-100 transition-colors group relative"
                                  aria-label={t('teamMembers.actions.editAriaLabel', { email: member.email })}
                                >
                                  <Edit className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-primary-600 group-hover:text-primary-700" />
                                  {/* Custom tooltip */}
                                  <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 bg-primary-100 text-primary-800 text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-50 whitespace-nowrap">
                                    {t('common.modify', 'Modifier')}
                                    <div className="absolute top-full left-1/2 -translate-x-1/2 w-0 h-0 border-l-2 border-r-2 border-t-2 border-l-transparent border-r-transparent border-t-primary-100"></div>
                                  </div>
                                </button>
                                <button 
                                  onClick={() => handleDelete(member)}
                                  className="p-1.5 sm:p-2 rounded-lg hover:bg-red-100 transition-colors group relative"
                                  aria-label={t('teamMembers.actions.deleteAriaLabel', { email: member.email })}
                                >
                                  <Trash2 className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-red-500 group-hover:text-red-700" />
                                  {/* Custom tooltip */}
                                  <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 bg-red-100 text-red-800 text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-50 whitespace-nowrap">
                                    {t('common.delete', 'Supprimer')}
                                    <div className="absolute top-full left-1/2 -translate-x-1/2 w-0 h-0 border-l-2 border-r-2 border-t-2 border-l-transparent border-r-transparent border-t-red-100"></div>
                                  </div>
                                </button>
                              </div>
                            </td>
                          </tr>
                          
                          {expandedMembers[member.id] && (
                            <tr className="bg-gray-50">
                              <td colSpan={4} className="px-2 sm:px-3 md:px-6 py-2 sm:py-3 md:py-4">
                                <div className="w-full">
                                  <h4 className="text-xs md:text-sm font-medium text-gray-900 mb-2 md:mb-3">
                                    {t('teamMembers.brandAccess.title')}
                                  </h4>
                                  <div className="space-y-2 md:space-y-3 max-w-full overflow-x-auto">
                                    {brandsUsedByMembers.map((brand: Brand) => {
                                      const memberBrand = member.brands.find(b => b.id === brand.id);
                                      
                                      if (memberBrand) {
                                        return (
                                          <div key={brand.id} className="flex items-center justify-between bg-white p-3 rounded-lg border border-gray-200">
                                            <div className="flex items-center flex-1 min-w-0 mr-4">
                                              {brand.logoUrl ? (
                                                <img 
                                                  src={brand.logoUrl} 
                                                  alt={brand.name} 
                                                  className="w-8 h-8 rounded-full object-contain mr-3 flex-shrink-0"
                                                />
                                              ) : (
                                                <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center mr-3 flex-shrink-0 text-xs font-bold">
                                                  {brand.name.charAt(0)}
                                                </div>
                                              )}
                                              <span className="font-medium text-sm truncate" title={brand.name}>{brand.name}</span>
                                            </div>
                                            
                                            <div className="flex items-center space-x-3 flex-shrink-0">
                                              <RoleTag role={memberBrand.role} />
                                              <StatusTag status={memberBrand.status} />
                                              <button 
                                                className="p-1 text-red-600 hover:text-red-800"
                                                onClick={async () => {
                                                    console.log('Remove access requested:', member.id, brand.id);
                                                    // TODO: Implement API call using useMutation
                                                    // DELETE /management/teams/{memberId}/brands/{brandId}
                                                    // On success: queryClient.invalidateQueries({ queryKey: ['teamMembers'] });
                                                }}
                                                aria-label={`Remove access to ${brand.name}`}
                                              >
                                                <Trash2 size={20} />
                                              </button>
                                            </div>
                                          </div>
                                        );
                                      } else {
                                        return null; 
                                      }
                                    })}
                                  </div>
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
    </PageLayout>

      <MemberModal
        isOpen={isAddModalOpen}
        onClose={handleCloseInviteModal}
        onSubmit={(data) => {
          console.log('Add member modal submitted:', data);
          setLocalError(null); // Clear any previous errors
          if (data.emails && data.emails.length > 0 && data.brandId && data.role) {
            const selectedBrand = allAvailableBrands.find(brand => brand.id === data.brandId);
            
            // Send invitation and wait for response
            inviteMutation.mutate({ 
              emails: data.emails, 
              brandId: data.brandId, 
              role: data.role,
              orgRole: (data as any).orgRole
            }, {
              onSuccess: () => {
                // Show success animation only after API success
                setSuccessInviteDetails({
                  emails: data.emails || [],
                  brandName: selectedBrand?.name || 'Unknown Brand',
                  action: 'invite'
                });
                setShowSuccessAnimation(true);
                
                // Close modal after success
                handleCloseInviteModal();

                // Ensure fresh data (explicit refresh in addition to hook-level invalidation)
                queryClient.invalidateQueries({ queryKey: ['teamMembers'] });
                
                // Auto hide animation after 4 seconds
                setTimeout(() => {
                  setShowSuccessAnimation(false);
                }, 4000);
              },
              onError: (error) => {
                // Handle error - keep modal open and show error
                setLocalError(error.message || "Failed to send invitations");
                console.error('Invitation error:', error);
              }
            });
          } else {
            console.error('Missing data required for invitation:', data);
            setLocalError("Missing email, brand, or role.");
          }
        }}
        brands={allAvailableBrands}
        mode="add"
      />

      <MemberModal
        isOpen={isModifyModalOpen}
        onClose={() => setIsModifyModalOpen(false)}
        onSubmit={async (data) => {
          console.log('Modify member data:', selectedMember?.id, data);
          if (!selectedMember) return;
          let token: string | null | undefined = null;
          try {
              token = await auth.currentUser?.getIdToken();
              if (!token) throw new Error("Authentication token is missing.");

              // Compute differences and map by brand
              const originalBrandIds = (selectedMember.brands || []).map(b => b.id);
              const newBrandIds = data.brandIds || [];
              const brandsToAdd = newBrandIds.filter(id => !originalBrandIds.includes(id));
              const brandsToRemove = originalBrandIds.filter(id => !newBrandIds.includes(id));
              const originalRoleByBrand: Record<string, string> = Object.fromEntries(
                (selectedMember.brands || []).map(b => [b.id, b.role])
              );
              const roleChangedBrandIds = newBrandIds
                .filter(id => originalBrandIds.includes(id))
                .filter(id => (originalRoleByBrand[id] || 'writer') !== (data.role || originalRoleByBrand[id]));

              // Build bulk payload: "add" handles both real adds and role updates on existing memberships
              const addSet = new Set<string>([...brandsToAdd, ...roleChangedBrandIds]);
              const addPayload = Array.from(addSet).map(brandId => ({
                brandId,
                role: data.role || (originalRoleByBrand[brandId] || 'writer'),
                status: 'active',
              }));

              if (addPayload.length === 0 && brandsToRemove.length === 0) {
                console.log('No changes detected. Skipping update.');
                setIsModifyModalOpen(false);
                return;
              }

              const headers = {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
              } as const;

              const bulkResp = await fetch(`https://api.socialness.ai/management/teams/members/${selectedMember.id}/brands`, {
                method: 'PATCH',
                headers,
                body: JSON.stringify({
                  add: addPayload.length ? addPayload : undefined,
                  remove: brandsToRemove.length ? brandsToRemove : undefined,
                  email: addPayload.length ? selectedMember.email : undefined,
                }),
              });

              if (!bulkResp.ok) {
                let message = `Failed to update member brands (status: ${bulkResp.status})`;
                try { const j = await bulkResp.json(); message = j.message || message; } catch {}
                throw new Error(message);
              }

              setIsModifyModalOpen(false);
              await queryClient.invalidateQueries({ queryKey: ['teamMembers'] });
            } catch (err) {
                console.error("Error modifying member:", err);
                setLocalError(err instanceof Error ? err.message : "Failed to modify member.");
            }
        }}
        brands={allAvailableBrands}
        mode="modify"
        initialData={{
          email: selectedMember?.email || '',
          brandIds: selectedMember?.brands.map(b => b.id) || [],
          role: selectedMember?.brands[0]?.role || 'writer'
        }}
      />

      <RemoveMemberModal
        isOpen={isRemoveModalOpen}
        onClose={() => setIsRemoveModalOpen(false)}
        onRemoveFromBrand={() => {
          setIsRemoveModalOpen(false);
          setIsDeleteModalOpen(true);
        }}
        onRemoveFromOrganisation={async () => {
          if (!selectedMember) return;
          try {
            const token = await auth.currentUser?.getIdToken();
            if (!token) throw new Error('Authentication token is missing.');
            const resp = await fetch(`https://api.socialness.ai/management/organisations/members/${selectedMember.id}`, {
              method: 'DELETE',
              headers: { 'Authorization': `Bearer ${token}` },
            });
            if (!resp.ok) {
              const text = await resp.text();
              throw new Error(text || `Failed to remove from organisation (status: ${resp.status})`);
            }
            // success popup
            setSuccessInviteDetails({ emails: [selectedMember.email], brandName: 'Organisation', action: 'delete' });
            setShowSuccessAnimation(true);
            setSelectedMember(null);
            setIsRemoveModalOpen(false);
            await queryClient.invalidateQueries({ queryKey: ['teamMembers'] });
            setTimeout(() => setShowSuccessAnimation(false), 4000);
          } catch (err) {
            setLocalError(err instanceof Error ? err.message : 'Failed to remove from organisation');
          }
        }}
        email={selectedMember?.email || ''}
        brandName={selectedMember?.brands?.[0]?.name}
      />

      <DeleteConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={handleConfirmDelete}
        title={t('teamMembers.deleteConfirmation.title')}
        message={t('teamMembers.deleteConfirmation.message', { 
          email: selectedMember?.email || '', 
          brandName: selectedMember?.brands?.[0]?.name || 'Unknown Brand'
        })}
        warning={t('teamMembers.deleteConfirmation.warning')}
      />

      <UpgradeModal
        isOpen={isUpgradeModalOpen}
        onClose={() => setModalParam('')}
        onUpgrade={() => window.location.assign('/billing')}
        resourceKey="users"
      />

      {/* Success Animation - Same as Post Publishing */}
      {showSuccessAnimation && successInviteDetails && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-xl shadow-2xl p-12 flex flex-col items-center justify-center transform transition-all duration-500">
            <div className="relative mb-4">
              <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center">
                <CheckCircle className="w-12 h-12 text-green-600" />
              </div>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Success!</h2>
            <div className="text-center text-gray-600 mb-4">
              {successInviteDetails.action === 'delete' ? (
                <>
                  <p className="font-medium">Successfully removed the following member from your brand <span className="font-bold text-gray-900">{successInviteDetails.brandName}</span>:</p>
                  <div className="mt-3 text-base">
                    {successInviteDetails.emails.map((email, index) => (
                      <div key={index} className="font-bold text-gray-800">• {email}</div>
                    ))}
                  </div>
                </>
              ) : (
                <>
                  <p className="font-medium">Congrats! You invited the following people to join your brand <span className="font-bold text-gray-900">{successInviteDetails.brandName}</span>:</p>
                  <div className="mt-3 text-base">
                    {successInviteDetails.emails.map((email, index) => (
                      <div key={index} className="font-bold text-gray-800">• {email}</div>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default TeamMembers;